import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { ChangeEvent, useCallback, useEffect, useRef, useState } from "react"
import classNames from "classnames"
import { selectActivatedFireId } from "features/map/control/selectors"
import {
    deriveTextName,
    selectIsDraggingText,
    selectIsEditingText,
    selectLocalText,
    setIsDraggingText,
    setIsEditingText,
    setLocalText,
    setTextDimensions,
} from "./slice"
import { scaleDownFontSize } from "./module"
import { useDebounceDispatch } from "tools/store"
import Moveable, { OnDrag, OnDragStart, OnRotate, OnScale, OnScaleStart } from "react-moveable"
import { useMap } from "react-map-gl"
import { TextInput } from "./types"
import { decodeNumberToColor } from "@nova/serializer/src/client/util"
import { updateText } from "features/pubsub/entityActions/cubes/text"
import { clearSelection } from "panels/content/side-panel/slice.new"
import { defaultTextToolContent, LineWidth, minZoom, navbarHeight } from "@nova/constants"
import { updateTextTemplate } from "../tool-registry"
import { ibmPlexSans } from "configuration/fonts"
import { getViewport, useViewport } from "features/map/GlobalViewState"
import { useEffectUnsafe } from "@nova/use-effect-unsafe"
import { useResizableSidePanelWidth } from "panels/content/side-panel/SidePanel"

export const textOutlineWidthMap: Record<LineWidth, number> = {
    [LineWidth.none]: 0,
    [LineWidth.normal]: 1.5,
    [LineWidth.thick]: 3.5,
}

//depending on which corner was chosen to scale with, we want the transform origin to be the opposite corner
const getTransformOrigin = (direction: number[]) => {
    const [dx, dy] = direction
    let originX = "50%"
    let originY = "50%"

    if (dx === -1) {
        originX = "100%"
    } else if (dx === 1) {
        originX = "0%"
    }

    if (dy === -1) {
        originY = "100%"
    } else if (dy === 1) {
        originY = "0%"
    }

    return `${originX} ${originY}`
}

const convertRotationValueToRotationPosition = (rotation: number) => {
    // Normalize the angle between 0 and 360 degrees
    let angle = rotation % 360
    if (angle < 0) {
        angle += 360
    }

    // Determine if angle is between 90 and 270 degrees
    if (angle >= 90 && angle <= 270) {
        return "top"
    } else {
        return "bottom"
    }
}

const getInputOffset = (rotationDegrees: number) => {
    const rotationRadians = (rotationDegrees * Math.PI) / 180

    const offsetX = -9
    const offsetY = -1

    const cosTheta = Math.cos(rotationRadians)
    const sinTheta = Math.sin(rotationRadians)

    const rotatedOffsetX = offsetX * cosTheta - offsetY * sinTheta
    const rotatedOffsetY = offsetX * sinTheta + offsetY * cosTheta

    return { topOffset: rotatedOffsetY, leftOffset: rotatedOffsetX }
}

export const TextToolInput = ({
    text,
    zoomLevel,
    onClick,
}: {
    text: TextInput
    zoomLevel: number
    onClick?: () => void
}) => {
    const dispatch = useAppDispatch()
    const { debounceDispatch } = useDebounceDispatch(50)
    const projectId = useAppSelector(selectActivatedFireId) || ""

    const localText = useAppSelector(selectLocalText()) || text
    const initialTextRef = useRef(localText.text)

    const outlineMultiplier = textOutlineWidthMap[localText.outlineWidth as LineWidth]
    const hasNoOutline = localText.outlineWidth === LineWidth.none

    const isEditing = useAppSelector(selectIsEditingText())
    const isDragging = useAppSelector(selectIsDraggingText())

    const [transform, setTransform] = useState({
        translate: [0, 0],
        rotation: 0,
        scale: [1, 1],
    })
    const [coordinate, setCoordinate] = useState({
        lng: text.longitude || 0,
        lat: text.latitude || 0,
    })

    const [rotation, _setRotation] = useState(text.rotation || 0)
    const rotationRef = useRef(text.rotation || 0)
    const setRotation = (rotation: number) => {
        _setRotation(rotation)
        rotationRef.current = rotation
    }
    useEffectUnsafe(() => {
        // Whenever a different text becomes active, reset the baseline.
        initialTextRef.current = localText.text
    }, [localText.id])

    const startingTextContent = initialTextRef.current

    // Listen to rotation changes from protobuf or inspector panel.
    useEffect(() => {
        if (rotationRef.current === text.rotation) return
        setRotation(text.rotation)
    }, [text.rotation])

    const [transformOrigin, setTransformOrigin] = useState("0% 100%")

    useEffect(() => {
        if (text.id !== localText.id) dispatch(setLocalText(undefined))
        else dispatch(setLocalText({ ...text, text: localText.text }))
    }, [text, dispatch, localText.text, localText.id])

    const mapRef = useMap()

    const inputRef = useRef<HTMLTextAreaElement>(null)
    const moveableRef = useRef<InstanceType<typeof Moveable>>(null)
    const spanRef = useRef<HTMLDivElement>(null)
    const anchorPointRef = useRef<HTMLDivElement>(null)

    const scalingRef = useRef(text.fontSize)

    useEffect(() => {
        const textarea = inputRef.current
        if (textarea) {
            textarea.style.width = `${textarea.scrollWidth * 2}px`
        }
    }, [localText?.text, localText?.fontSize])

    const view = useViewport()
    const position = view.project([coordinate.lng, coordinate.lat])
    const adjustedRotation = rotation - (view.bearing || 0)
    const fontSize = scaleDownFontSize(localText?.fontSize || text.fontSize, 512, text?.latitude)

    const outlineWidthPx = (outlineMultiplier * 0.5 * fontSize) / 100
    const shadowOffset = Math.max(0, Math.round(outlineWidthPx))

    // Because shadows are being used to simulate text outline, it tries to hide it behind the text when set to 0
    // This breaks when the user scrolls out, so the fix here is to set the shadowColor to be text color
    // on the editor when we expect no outline
    const shadowColor = decodeNumberToColor(
        hasNoOutline ? localText.color : (localText.outlineColor ?? 1)
    )

    let top = position[1] + transform.translate[1]
    let left = position[0] + transform.translate[0]
    const { topOffset, leftOffset } = getInputOffset(adjustedRotation)
    top += topOffset
    left += leftOffset

    useEffect(() => {
        const handleKeyEvents = (event: KeyboardEvent) => {
            switch (event.key) {
                case "Escape":
                    if (isEditing) dispatch(setIsEditingText(false))
                    else dispatch(clearSelection())
                    break
                case "[": {
                    const size = localText.fontSize * 0.95
                    dispatch(
                        updateText({
                            id: localText.id,
                            updates: { fontSize: +size.toFixed(0) },
                        })
                    )
                    dispatch(
                        updateTextTemplate({
                            fontSize: {
                                value: +size.toFixed(0),
                                zoomLevel: getViewport().zoom || minZoom,
                            },
                        })
                    )
                    event.preventDefault()
                    break
                }
                case "]": {
                    const size = localText.fontSize * 1.05
                    dispatch(
                        updateText({
                            id: localText.id,
                            updates: { fontSize: +size.toFixed(0) },
                        })
                    )
                    dispatch(
                        updateTextTemplate({
                            fontSize: {
                                value: +size.toFixed(0),
                                zoomLevel: getViewport().zoom || minZoom,
                            },
                        })
                    )
                    event.preventDefault()
                    break
                }
                case "a": {
                    // Prevents select all from selecting both the inspector modal content
                    if (event.metaKey) {
                        const textarea = inputRef.current
                        // Checking if current view is a state between interacting with the modal or the textarea editor
                        if (textarea && document.activeElement instanceof HTMLCanvasElement) {
                            event.stopPropagation()
                            textarea.select()
                            event.preventDefault()
                        }
                    }
                    break
                }
            }
        }

        document.addEventListener("keydown", handleKeyEvents)
        return () => {
            document.removeEventListener("keydown", handleKeyEvents)
        }
    }, [localText, isEditing, dispatch, onClick, projectId])

    useEffect(() => {
        const handleMapZoom = (event: WheelEvent) => {
            event.preventDefault()
            mapRef.current?.getMap().scrollZoom.wheel(event)
        }
        const textarea = inputRef.current
        if (!textarea) return
        textarea.addEventListener("wheel", handleMapZoom)

        return () => {
            textarea.removeEventListener("wheel", handleMapZoom)
        }
    }, [dispatch, mapRef])

    useEffect(() => {
        const textArea = inputRef.current
        if (!textArea) return
        if (isEditing) {
            textArea.focus()
            textArea.setSelectionRange(textArea.value.length, textArea.value.length) // Move cursor to the end
        }
        if (!isEditing) {
            textArea.blur()
        }
    }, [isEditing])

    const setTextDragging = useCallback(() => dispatch(setIsDraggingText(true)), [dispatch])
    const setTextDoneDragging = useCallback(() => dispatch(setIsDraggingText(false)), [dispatch])

    //this useEffect works around a lot of the quirkiness involved with dragging and scaling stuttering when the operations are performed
    //we need to delay finishing these operations so that they appear much smoother
    useEffect(() => {
        setTextDoneDragging()
        setTransform((prev) => ({
            ...prev,
            scale: [1, 1],
        }))
        if (moveableRef.current) {
            setTimeout(() => {
                moveableRef.current?.updateRect()
                scalingRef.current = localText.fontSize
            }, 0)
        }
    }, [
        localText.longitude,
        localText.latitude,
        localText.fontSize,
        localText.rotation,
        setTextDoneDragging,
    ])

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault()

            dispatch(
                updateText({
                    id: text.id,
                    updates: {
                        text: localText.text,
                        name: deriveTextName(localText.name, startingTextContent, localText.text),
                    },
                })
            )
            dispatch(setIsEditingText(false))
        }
    }

    const handleInputChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
        const sanitizedValue = e.target.value.replace(
            /[^A-Za-z0-9 !@#$%^&*()_+\-=\[\]{};':`~"\\|,.<>\/?\r\n]/g,
            ""
        )
        dispatch(setLocalText({ ...localText, text: sanitizedValue }))
    }

    const initialDragOffset = useRef([0, 0])
    const sidePanelWidth = useResizableSidePanelWidth()

    const handleDragStart = (event: OnDragStart) => {
        const { clientX, clientY } = event
        const anchorPoint = anchorPointRef.current?.getBoundingClientRect()
        if (!anchorPoint) return
        initialDragOffset.current = [
            clientX - anchorPoint.left + sidePanelWidth,
            clientY - anchorPoint.top + navbarHeight,
        ]
    }

    const handleDrag = (event: OnDrag) => {
        if (!isDragging) setTextDragging()
        const { clientX, clientY } = event
        const translateX = clientX - position[0] - initialDragOffset.current[0]
        const translateY = clientY - position[1] - initialDragOffset.current[1]
        setTransform((prev) => ({
            ...prev,
            translate: [translateX, translateY],
        }))
    }

    const handleDragEnd = () => {
        if (!isDragging) return
        const translate = transform.translate
        const originalPosition = position
        const newPosition = [originalPosition[0] + translate[0], originalPosition[1] + translate[1]]

        const newCoords = getViewport().unproject([newPosition[0], newPosition[1]])
        dispatch(
            updateText({
                id: localText.id,
                updates: {
                    longitude: newCoords[0],
                    latitude: newCoords[1],
                },
            })
        )
        setCoordinate({ lng: newCoords[0], lat: newCoords[1] })
        setTransform((prev) => ({
            ...prev,
            translate: [0, 0],
        }))
    }

    const handleRotate = (e: OnRotate) => {
        const isShiftKeyPressed = e.inputEvent.shiftKey
        let newRotate = e.rotation

        if (isShiftKeyPressed) {
            let angle = rotation % 360
            if (angle < 0) {
                angle += 360
            }

            const nearestIncrement = Math.round((newRotate + angle) / 15) * 15
            newRotate = nearestIncrement - angle
        }

        setTransform((prev) => ({
            ...prev,
            rotation: newRotate,
        }))
    }

    const handleRotateEnd = () => {
        if (anchorPointRef.current) {
            const rect = anchorPointRef.current.getBoundingClientRect()

            const newCoords = getViewport().unproject([
                rect.left - sidePanelWidth,
                rect.top - navbarHeight,
            ])

            let newRotation = rotation + transform.rotation
            newRotation = Math.floor(newRotation % 360)
            if (newRotation < 0) newRotation = 360 + newRotation

            dispatch(
                updateText({
                    id: localText.id,
                    updates: {
                        longitude: newCoords[0],
                        latitude: newCoords[1],
                        rotation: newRotation,
                    },
                })
            )
            setRotation(newRotation)
            setCoordinate({ lng: newCoords[0], lat: newCoords[1] })
            setTransform((prev) => ({
                ...prev,
                rotation: 0,
            }))
        }
    }

    const handleScaleStart = ({ direction }: OnScaleStart) => {
        const origin = getTransformOrigin(direction)
        setTransformOrigin(origin)
        setTextDragging()
    }

    const handleScale = (event: OnScale) => {
        const { scale } = event
        if (
            scale[0] <= 0 ||
            localText.fontSize * scale[0] > 500000 ||
            localText.fontSize * scale[0] < 1
        )
            return
        setTransform((prev) => ({
            ...prev,
            scale,
        }))
    }

    const handleScaleEnd = () => {
        if (anchorPointRef.current) {
            const rect = anchorPointRef.current.getBoundingClientRect()

            const newCoords = getViewport().unproject([
                rect.left - sidePanelWidth,
                rect.top - navbarHeight,
            ])
            const newFontSize = Math.floor(transform.scale[0] * localText.fontSize)

            dispatch(
                updateText({
                    id: localText.id,
                    updates: {
                        longitude: newCoords[0],
                        latitude: newCoords[1],
                        fontSize: newFontSize,
                        baseFontSize: newFontSize,
                    },
                })
            )
            dispatch(
                updateTextTemplate({
                    fontSize: { value: newFontSize, zoomLevel: getViewport().zoom || minZoom },
                })
            )
            setCoordinate({ lng: newCoords[0], lat: newCoords[1] })
        }
        setTransformOrigin("0% 100%")
    }

    const adjustSize = useCallback(() => {
        const textarea = inputRef.current
        const span = spanRef.current

        if (textarea && span) {
            // Set span text to match textarea's value for width calculation
            const rows = textarea.value.split("\n")
            let longestRow = ""
            for (const row of rows) {
                if (row.length > longestRow.length) {
                    longestRow = row
                }
            }
            span.textContent =
                localText.text && longestRow.length > 0 ? longestRow : defaultTextToolContent

            // Adjust width based on span's width (it mimics the content of the textarea)
            const padding = localText.bold ? 10 : 7
            const width = span.offsetWidth + padding
            textarea.style.width = `${width}px` // Adding a small buffer for padding

            // Adjust height
            textarea.style.height = "auto" // Reset height to calculate properly
            const height = textarea.scrollHeight
            textarea.style.height = `${height}px`
            debounceDispatch(setTextDimensions({ id: localText.id, dimensions: { height, width } }))
        }
    }, [localText.text, localText.bold, localText.id, debounceDispatch])

    // Adjust height whenever text changes
    useEffect(() => {
        adjustSize()

        // Force Moveable to update its internal rect
        if (moveableRef.current) {
            moveableRef.current.updateRect()
        }
    }, [localText.text, localText.fontSize, localText.bold, zoomLevel, adjustSize])

    const scale = scalingRef.current === localText.fontSize ? transform.scale : [1, 1]
    return (
        <>
            {/* this extra div is necessary to determine the width of what the textarea should be. Seems hacky but this is what chatgpt suggested at least lol */}
            <div
                ref={spanRef}
                className="px-1.5 py-1 -z-[1]"
                style={{
                    position: "absolute",
                    visibility: "hidden",
                    whiteSpace: "nowrap",
                    wordWrap: "break-word",
                    fontWeight: localText?.bold ? "700" : "400",
                    fontFamily: ibmPlexSans.style.fontFamily,
                    fontSize: `${fontSize}px`,
                    lineHeight: "1.5",
                }}
            />
            <div
                style={{
                    position: "absolute",
                    transformOrigin: "0% 100%",
                    transform: `translate(${left}px, calc(${top}px - 100%)) rotate(${adjustedRotation}deg)`,
                }}
            >
                <div
                    style={{
                        transformOrigin: "50% 50%",
                        transform: `rotate(${transform.rotation}deg)`,
                    }}
                >
                    <div
                        style={{
                            transformOrigin: transformOrigin,
                            transform: `
                    scale(${scale[0]}, ${scale[1]})
                  `,
                        }}
                    >
                        <textarea
                            id={`text-tool-${localText.id}`}
                            ref={inputRef}
                            className={classNames(
                                "mx-1.5 p-0 bg-transparent mt-1 w-full h-max focus:ring-0 rounded-md border-[#2C56ED] border-dashed border-[2px]",
                                { "!cursor-pointer": !isEditing },
                                { "!cursor-move": isDragging }
                            )}
                            style={{
                                lineHeight: "1.5",
                                fontWeight: localText?.bold ? "700" : "400",
                                fontFamily: ibmPlexSans.style.fontFamily,
                                fontSize: `${fontSize}px`,
                                textShadow: `
                                    -${shadowOffset}px -${shadowOffset}px 0 ${shadowColor},
                                    ${shadowOffset}px -${shadowOffset}px 0 ${shadowColor},
                                    -${shadowOffset}px  ${shadowOffset}px 0 ${shadowColor},
                                    ${shadowOffset}px  ${shadowOffset}px 0 ${shadowColor}
                                `,
                                color:
                                    localText?.color !== undefined
                                        ? decodeNumberToColor(localText.color)
                                        : "#000",
                                overflow: "hidden",
                                resize: "none",
                                display: "block",
                                textAlign: "left",
                                verticalAlign: "bottom",
                            }}
                            rows={1}
                            value={
                                localText.text !== defaultTextToolContent
                                    ? localText.text
                                    : undefined
                            }
                            placeholder={defaultTextToolContent}
                            onKeyDown={handleKeyDown}
                            onChange={handleInputChange}
                            readOnly={!isEditing}
                            autoFocus={isEditing}
                            draggable={false}
                            spellCheck={false}
                            onClick={onClick}
                        />

                        {/* a div that tracks the exact position of the middle left anchor point of the text box */}
                        <div
                            ref={anchorPointRef}
                            className="absolute"
                            style={{ transform: `translate(${9}px, calc(100% + 1px))` }}
                        />
                    </div>
                </div>
            </div>

            <Moveable
                key={`${position[0]}${position[1]}`}
                className={classNames("moveable-text-area", { hidden: !isEditing })}
                ref={moveableRef}
                target={inputRef.current}
                renderDirections={["nw", "ne", "sw", "se"]}
                onScaleStart={handleScaleStart}
                onScale={handleScale}
                onScaleEnd={handleScaleEnd}
                onRotateStart={setTextDragging}
                onRotate={handleRotate}
                onRotateEnd={handleRotateEnd}
                onDragStart={handleDragStart}
                onDrag={handleDrag}
                onDragEnd={handleDragEnd}
                onClick={() => dispatch(setIsEditingText(true))}
                rotationPosition={convertRotationValueToRotationPosition(localText.rotation || 0)}
                origin={false}
                draggable={!isEditing}
                scalable={!isEditing}
                rotatable={!isEditing}
                keepRatio
            />
        </>
    )
}
